export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
    public readonly retryAfter?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type QueryValue = string | number | boolean | undefined | null;

export type ApiOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** Do not broadcast 401s (used by the session probe and the login calls). */
  skipAuthRedirect?: boolean;
};

export const UNAUTHORIZED_EVENT = "fin:unauthorized";

export function buildQuery(query: Record<string, QueryValue>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  return params.toString();
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const qs = options.query ? buildQuery(options.query) : "";
  const url = qs ? `${path}?${qs}` : path;
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json", "x-requested-with": "fin" };
  let body: string | undefined;
  if (method !== "GET") {
    headers["content-type"] = "application/json";
    body = JSON.stringify(options.body ?? {});
  }
  let res: Response;
  try {
    res = await fetch(url, { method, headers, body, credentials: "same-origin", signal: options.signal });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "network", "No se pudo conectar. Inténtalo de nuevo.");
  }
  if (res.status === 401 && !options.skipAuthRedirect) {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
  }
  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as
      | { error?: { code?: string; message?: string; [k: string]: unknown } }
      | null;
    const error = payload?.error ?? {};
    const { code, message, ...details } = error;
    const retryAfter = Number(res.headers.get("retry-after")) || (details.retryAfterSeconds as number | undefined);
    throw new ApiError(
      res.status,
      typeof code === "string" ? code : "http_error",
      typeof message === "string" ? message : `Error ${res.status}`,
      details,
      retryAfter || undefined,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
